import {useState,useEffect,useRef,memo,useMemo} from 'react'
import {Tooltip, Drawer,Typography,Stack, Button, TextField, InputAdornment, CircularProgress, Checkbox, ToggleButton, ToggleButtonGroup, Select, MenuItem} from '@mui/material';
import ImageListItemBar from '@mui/material/ImageListItemBar';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import SuggestionsItemPanel from './suggestionsItemPanel.jsx';
import CreatorFilter from './creatorFilter';
import { fetchHomelessItems, fetchHomelessCount, searchItems, markUnfit, markItemsUnfit } from '../supabaseApi';
import IconButton from '@mui/material/IconButton';
import HoverPreview from './hoverPreview';
import useCachedImage from '../hooks/useCachedImage'
import { t } from '../utils/strings';
import { accent, accentSurface } from '../utils/colorDict';

const HomelessThumb = memo(({ item, selectMode, isSelected, toggleSelect, handleModalData, handleUnfitSingle, handleRestoreSingle, showUnfit, itemToPosition, handleItemToPosition, handleItemsToMark, cancelBeam, authData, lang }) => {
  const imgUrl = item.properties.right_panel_url || item.properties.thumbnail_url
  const imgFallback = item.properties.right_panel_fallback || item.properties.thumbnail_fallback
  const isQuestionable = Array.isArray(item.properties.questionable_by) ? item.properties.questionable_by.length > 0 : false
  const { image, isError } = useCachedImage(imgUrl || null, imgFallback || null)
  const blobSrc = useMemo(() => image ? URL.createObjectURL(image) : null, [image])
  useEffect(() => { return () => { if (blobSrc) URL.revokeObjectURL(blobSrc) } }, [blobSrc])

    // HoverPreview is suppressed while geotagging, as in searchWidget. The
    // preview is a portal on document.body with pointerEvents:'none', so once
    // the cursor moves onto the map to set a bearing it never receives a
    // mouseleave - it would otherwise hang over the map until the next
    // re-render (a zoom or pan).
    return (
    <HoverPreview previewUrl={item.properties.modal_url} style={{ width: 'calc(33.33% - 3px)' }} disabled={!blobSrc || !!itemToPosition} previewHeight={300} title={(lang === 'he' && item.properties.title_heb) ? item.properties.title_heb : item.properties.title}>
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: blobSrc ? '#444' : '#e0e0e0',
        outline: isSelected ? `3px solid ${accent}` : 'none',
        cursor: selectMode ? 'pointer' : 'default',
        overflow: 'hidden'
      }}
      onClick={() => { if (selectMode) toggleSelect(item.properties.id) }}
    >
      {selectMode && <Checkbox checked={isSelected} size="small"
        sx={{ position: 'absolute', top: 0, left: 0, zIndex: 2, color: 'white', '&.Mui-checked': { color: accent } }}
        onClick={(e) => e.stopPropagation()}
        onChange={() => toggleSelect(item.properties.id)} />}
      {isQuestionable && <ReportProblemIcon sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2, color: '#ff9800', fontSize: 18 }} />}

      {!blobSrc && !isError && !(!imgUrl) && <CircularProgress size={24} sx={{ position: 'absolute' }} />}
      {(isError || !imgUrl) && !blobSrc && <BrokenImageIcon sx={{ position: 'absolute', color: '#bbb', fontSize: 28 }} />}
      {blobSrc && <img src={blobSrc}
        alt=""
        style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain', cursor: 'pointer' }}
        onClick={(e) => { if (!selectMode) { e.stopPropagation(); handleModalData(item, item.properties.mediatype) } }}
      />}
      {(blobSrc || isError || !imgUrl) && <ImageListItemBar
        sx={{ padding: '0 6px', direction: 'ltr', '& .MuiImageListItemBar-title': { fontFamily: 'sans-serif', fontSize: '12px', textAlign: lang === 'he' ? 'right' : 'left', direction: lang === 'he' ? 'rtl' : 'ltr' } }}
        title={(lang === 'he' && item.properties.title_heb) ? item.properties.title_heb : item.properties.title}
        actionPosition={lang === 'he' ? 'left' : 'right'}
        actionIcon={!selectMode && (authData?.user?.role === 'admin' || authData?.user?.role === 'editor') ? <div style={{display:'flex'}}>
          {!showUnfit && <Tooltip title={t('addLocation', lang)}>
            <IconButton size="small"
              style={{ color: item === itemToPosition ? 'white' : '#90ee90' }}
              onClick={(e) => {
                e.stopPropagation()
                cancelBeam()
                if (item === itemToPosition) {
                  handleItemToPosition(null)
                  handleItemsToMark([])
                } else {
                  handleItemToPosition(item)
                }
              }}>
              <AddLocationAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>}
          {showUnfit ?
            <Tooltip title={t('restoreToTaggable', lang)}>
              <IconButton size="small" style={{ color: '#90ee90' }}
                onClick={(e) => { e.stopPropagation(); handleRestoreSingle(item) }}>
                <AddPhotoAlternateIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          :
            <Tooltip title={t('markAsUnfitForTagging', lang)}>
              <IconButton size="small" style={{ color: '#ffb74d' }}
                onClick={(e) => { e.stopPropagation(); handleUnfitSingle(item) }}>
                <ImageNotSupportedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        </div> : null}
      />}
    </div>
    </HoverPreview>
  )
});

const HomelessWidget = memo(({mapRef,
                        itemToPosition,
                        handleItemToPosition,
                        suggestions,
                        handleSuggestions,
                        itemToSuggest,
                        handleItemToSuggest,
                        rightPanel,
                        handleRightPanel,
                        cancelBeam,
                        handleItemsToMark,
                        handleModalData,
                        openModalWithList,
                        authData,
                        lastGeotaggedId,
                        lang,
                        creatorOptions,
                        widgetCreators,
                        setWidgetCreators,
                        })=>{

const BATCH_SIZE = 60
const [batchItems, setBatchItems] = useState([])
const [totalCount, setTotalCount] = useState(0)
const [batch, setBatch] = useState(0)
const [filterText, setFilterText] = useState('')
const [searchResults, setSearchResults] = useState(null)
const [searching, setSearching] = useState(false)
const [selectMode, setSelectMode] = useState(false)
const [selectedIds, setSelectedIds] = useState(new Set())
const [showUnfit, setShowUnfit] = useState(false)
const [loading, setLoading] = useState(false)
const scrollRef = useRef(null)
const searchTimerRef = useRef(null)

const isSearching = searchResults !== null
const activeTotal = isSearching ? searchResults.length : totalCount
const totalBatches = Math.max(1, Math.ceil(activeTotal / BATCH_SIZE))

const handleSearch = async () => {
  if (!filterText.trim()) {
    setSearchResults(null)
    setBatch(0)
    return
  }
  setSearching(true)
  setSearchResults([])
  setBatch(0)
  try {
    const creators = (Array.isArray(widgetCreators) && widgetCreators.length > 0) ? widgetCreators : undefined
    const results = await searchItems(filterText.trim(), creators)
    const homeless = results.filter(item => {
      if (item.geometry?.coordinates?.length) return false
      if (showUnfit) return item.properties.unfit_for_tagging === true
      return item.properties.unfit_for_tagging !== true
    })
    setSearchResults(homeless)
    setBatch(0)
  } catch (err) {
    console.log(err)
  } finally {
    setSearching(false)
  }
}

// Remove geotagged item from list only on confirmed geotag (not on ESC cancel)
useEffect(() => {
  if (!lastGeotaggedId) return
  setBatchItems(prev => prev.filter(i => i.properties.id !== lastGeotaggedId))
  setSearchResults(prev => prev ? prev.filter(i => i.properties.id !== lastGeotaggedId) : null)
  setTotalCount(prev => Math.max(0, prev - 1))
}, [lastGeotaggedId])

// Display items: for search results, slice client-side; for normal mode, use server-fetched batch
const displayItems = isSearching
  ? searchResults.slice(batch * BATCH_SIZE, (batch + 1) * BATCH_SIZE)
  : batchItems

// Stable key for widgetCreators in the dependency array — array identity changes
// every render even when contents are unchanged.
const creatorsKey = Array.isArray(widgetCreators) ? widgetCreators.slice().sort().join('|') : ''

// When the creator filter changes, jump back to page 0 — otherwise we'd be on
// page 5 of a now-smaller filtered set.
useEffect(() => {
  setBatch(0)
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [creatorsKey])

// While a search is active in the homeless widget, re-run it whenever the
// creator filter (or showUnfit toggle) changes so results stay in sync.
useEffect(() => {
  if (rightPanel !== 1) return
  if (!isSearching) return
  if (!filterText.trim()) return
  let cancelled = false
  ;(async () => {
    setSearching(true)
    try {
      const creators = (Array.isArray(widgetCreators) && widgetCreators.length > 0) ? widgetCreators : undefined
      const results = await searchItems(filterText.trim(), creators)
      if (cancelled) return
      const homeless = results.filter(item => {
        if (item.geometry?.coordinates?.length) return false
        if (showUnfit) return item.properties.unfit_for_tagging === true
        return item.properties.unfit_for_tagging !== true
      })
      setSearchResults(homeless)
      setBatch(0)
    } catch (err) {
      console.log(err)
    } finally {
      if (!cancelled) setSearching(false)
    }
  })()
  return () => { cancelled = true }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [creatorsKey, showUnfit, rightPanel])

// Fetch count + batch when panel opens, batch changes, unfit toggle changes,
// or creator selection changes.
useEffect(() => {
  if (rightPanel !== 1) return
  if (isSearching) return
  let cancelled = false
  const fetchBatch = async () => {
    setLoading(true)
    try {
      const creators = (Array.isArray(widgetCreators) && widgetCreators.length > 0) ? widgetCreators : undefined
      const count = await fetchHomelessCount(showUnfit, creators)
      if (cancelled) return
      setTotalCount(count)
      const features = await fetchHomelessItems(showUnfit, batch * BATCH_SIZE, BATCH_SIZE, creators)
      if (cancelled) return
      setBatchItems(features || [])
    } catch (err) {
      console.log(err)
    }
    if (!cancelled) setLoading(false)
  }
  fetchBatch()
  return () => { cancelled = true }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [rightPanel, showUnfit, batch, creatorsKey])

useEffect(() => {
  scrollRef.current?.scrollTo(0, 0)
}, [batch])

const toggleSelect = (id) => {
  setSelectedIds(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
}

const handleUnfitSingle = async (item) => {
  try {
    await markUnfit(item.properties.id, true)
    setBatchItems(prev => prev.filter(i => i.properties.id !== item.properties.id))
    setSearchResults(prev => prev ? prev.filter(i => i.properties.id !== item.properties.id) : null)
    setTotalCount(prev => Math.max(0, prev - 1))
  } catch (err) {
    alert('Failed: ' + (err?.message || 'Unknown error'))
  }
}

const handleRestoreSingle = async (item) => {
  try {
    await markUnfit(item.properties.id, false)
    setBatchItems(prev => prev.filter(i => i.properties.id !== item.properties.id))
    setSearchResults(prev => prev ? prev.filter(i => i.properties.id !== item.properties.id) : null)
    setTotalCount(prev => Math.max(0, prev - 1))
  } catch (err) {
    alert('Failed: ' + (err?.message || 'Unknown error'))
  }
}

const handleBulkAction = async () => {
  if (selectedIds.size === 0) return
  if (!window.confirm(`${showUnfit ? 'Restore' : 'Mark'} ${selectedIds.size} item(s) ${showUnfit ? 'to taggable' : 'as unfit'}?`)) return
  try {
    await markItemsUnfit([...selectedIds], !showUnfit)
    setBatchItems(prev => prev.filter(i => !selectedIds.has(i.properties.id)))
    setTotalCount(prev => Math.max(0, prev - selectedIds.size))
    setSelectedIds(new Set())
    setSelectMode(false)
  } catch (err) {
    alert('Failed: ' + (err?.message || 'Unknown error'))
  }
}

return <Drawer
PaperProps={{ style: {height: 'calc(100% - 80px)',top:'80px',zIndex:3,width:'34vw',overflowX:'hidden',background:'whitesmoke' }}}
variant="persistent"
anchor={'left'}
onClose={()=>{handleRightPanel(0)}}
open={rightPanel===1}
>

{itemToSuggest ?   <SuggestionsItemPanel
      handleItemToPosition={handleItemToPosition}
      handleItemToSuggest={handleItemToSuggest}
      itemToSuggest={itemToSuggest}
      rightPanel={rightPanel}
      mapRef={mapRef}
      suggestions={suggestions}
      itemToPosition={itemToPosition}
      handleSuggestions={handleSuggestions}
      handleItemsToMark={handleItemsToMark}
      /> :

<Stack sx={{backgroundColor:accentSurface, height:'100%', display:'flex', flexDirection:'column'}}>
<Stack sx={{padding:'10px 10px 0', alignItems:'center', flexShrink:0, boxShadow:'0 2px 4px rgba(0,0,0,0.1)', zIndex:1, backgroundColor:accentSurface}}>
<div style={{display:'flex', alignItems:'center', width:'100%', justifyContent:'center', gap:'8px'}}>
  <Typography variant='h6'>{t('photosForGeotagging', lang)}</Typography>
  {(authData?.user?.role === 'admin' || authData?.user?.role === 'editor') && <Tooltip title={selectMode ? t('exitSelectMode', lang) : t('selectMultiple', lang)}>
    <IconButton size="small" onClick={() => { setSelectMode(!selectMode); setSelectedIds(new Set()) }}
      sx={{ color: selectMode ? accent : 'inherit' }}>
      <ChecklistIcon />
    </IconButton>
  </Tooltip>}
  {/* Per-widget creator filter — independent of the header dropdown once open */}
  <CreatorFilter
    creatorOptions={creatorOptions}
    selected={Array.isArray(widgetCreators) ? widgetCreators : []}
    onChange={setWidgetCreators}
    lang={lang}
  />
</div>

{(authData?.user?.role === 'admin' || authData?.user?.role === 'editor') &&
  <ToggleButtonGroup
    value={showUnfit ? 'unfit' : 'taggable'}
    exclusive
    size="small"
    onChange={(e, val) => {
      if (val) {
        setShowUnfit(val === 'unfit')
        setSelectMode(false)
        setSelectedIds(new Set())
        setFilterText('')
        setSearchResults(null)
      }
    }}
    sx={{ margin: '6px 0' }}
  >
    <ToggleButton value="taggable" sx={{ textTransform: 'none', px: 2, py: 0.3, fontSize: '0.8rem' }}>
      {t('taggable', lang)}
    </ToggleButton>
    <ToggleButton value="unfit" sx={{ textTransform: 'none', px: 2, py: 0.3, fontSize: '0.8rem' }}>
      {t('unfit', lang)}
    </ToggleButton>
  </ToggleButtonGroup>
}

<TextField
  size="small"
  placeholder={t('filterByKeyword', lang)}
  value={filterText}
  onChange={(e) => setFilterText(e.target.value)}
  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
  inputProps={{
    style: {
      textAlign: (filterText ? /[\u0590-\u05FF]/.test(filterText) : lang === 'he') ? 'right' : 'left',
      direction: (filterText ? /[\u0590-\u05FF]/.test(filterText) : lang === 'he') ? 'rtl' : 'ltr'
    }
  }}
  sx={{width:'90%', margin:'8px 0', backgroundColor:'white', borderRadius:1}}
  InputProps={{
    startAdornment: <InputAdornment position="start">
      <IconButton size="small" onClick={handleSearch}><SearchIcon fontSize="small"/></IconButton>
    </InputAdornment>,
    endAdornment: filterText && (
      <InputAdornment position="end">
        <IconButton size="small" onClick={() => { setFilterText(''); setSearchResults(null); setBatch(0) }}>
          <ClearIcon fontSize="small"/>
        </IconButton>
      </InputAdornment>
    )
  }}
/>
{filterText && <Typography variant="caption" color="text.secondary" dir="auto" sx={{paddingBottom:'4px'}}>{searching ? t('searching', lang) : t('searchResultCount', lang, { count: displayItems.length })}</Typography>}

{selectMode && <div style={{display:'flex', gap:'8px', padding:'4px 0', alignItems:'center'}}>
  <Button size="small" onClick={() => setSelectedIds(new Set(displayItems.map(i => i.properties.id)))}>{t('selectAll', lang)}</Button>
  <Button size="small" onClick={() => setSelectedIds(new Set())}>{t('deselectAll', lang)}</Button>
  {selectedIds.size > 0 && <Button size="small" color={showUnfit ? 'success' : 'warning'} variant="contained"
    startIcon={showUnfit ? <AddPhotoAlternateIcon/> : <ImageNotSupportedIcon/>}
    onClick={handleBulkAction}>{showUnfit ? `Restore ${selectedIds.size}` : `Unfit ${selectedIds.size}`}</Button>}
</div>}

{/* Batch selector */}
{totalBatches > 1 && <div dir={lang === 'he' ? 'rtl' : 'ltr'} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', margin: '4px 0' }}>
  <IconButton size="small" style={{ color: accent }} disabled={batch === 0}
    onClick={() => setBatch(b => Math.max(0, b - 1))}>{lang === 'he' ? <ArrowRightIcon /> : <ArrowLeftIcon />}</IconButton>
  <Select size="small" value={batch} onChange={(e) => setBatch(e.target.value)}
    sx={{ minWidth: 60, fontSize: '0.85rem', '& .MuiSelect-select': { py: '2px' } }}>
    {Array.from({ length: totalBatches }, (_, i) => (
      <MenuItem key={i} value={i}>{i + 1}</MenuItem>
    ))}
  </Select>
  <Typography variant="caption" color="text.secondary">
    / {totalBatches}
  </Typography>
  <IconButton size="small" style={{ color: accent }} disabled={batch >= totalBatches - 1}
    onClick={() => setBatch(b => Math.min(totalBatches - 1, b + 1))}>{lang === 'he' ? <ArrowLeftIcon /> : <ArrowRightIcon />}</IconButton>
</div>}
{activeTotal > 0 && <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', pb: 0.5 }}>
  {t('itemsRange', lang, { from: batch * BATCH_SIZE + 1, to: Math.min((batch + 1) * BATCH_SIZE, activeTotal), total: activeTotal })}
</Typography>}
</Stack>

{loading ? <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
  <CircularProgress size={28} sx={{ color: accent }} />
</div> :
<div ref={scrollRef} dir={lang === 'he' ? 'rtl' : 'ltr'} style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '6px', overflow: 'auto', flex: 1, alignContent: 'flex-start' }}>
{displayItems.map((item) => (
  <HomelessThumb
    key={item.properties.id}
    item={item}
    selectMode={selectMode}
    isSelected={selectedIds.has(item.properties.id)}
    toggleSelect={toggleSelect}
    handleModalData={(item) => openModalWithList ? openModalWithList(item, displayItems) : handleModalData(item)}
    handleUnfitSingle={handleUnfitSingle}
    handleRestoreSingle={handleRestoreSingle}
    showUnfit={showUnfit}
    itemToPosition={itemToPosition}
    handleItemToPosition={handleItemToPosition}
    handleItemsToMark={handleItemsToMark}
    cancelBeam={cancelBeam}
    authData={authData}
    lang={lang}
  />
))}
</div>}
</Stack> }
</Drawer>

 });

export default HomelessWidget
